import React, { useState, useEffect, useRef, useCallback } from 'react';
import './index.css';
import { FaHeart, FaComment, FaShare, FaPlay, FaPlus, FaSpinner } from 'react-icons/fa';
import { IoClose, IoArrowBack } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const DEFAULT_AVATAR = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23a0a0a0'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/></svg>";

// ──────────────────────────────────────────────
// Single Reel Item Component
// ──────────────────────────────────────────────
const ReelItem = ({ reel, isActive, isMuted, onToggleMute, onOpenComments }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isLiked, setIsLiked] = useState(reel.is_liked || false);
  const [likesCount, setLikesCount] = useState(parseInt(reel.likes_count) || 0);
  const [showHeart, setShowHeart] = useState(false);
  const lastTap = useRef(0);

  // Video buffering event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onWaiting  = () => setIsBuffering(true);
    const onCanPlay  = () => setIsBuffering(false);
    const onPlaying  = () => { setIsBuffering(false); setIsPlaying(true); };
    const onStalled  = () => setIsBuffering(true);

    video.addEventListener('waiting',  onWaiting);
    video.addEventListener('canplay',  onCanPlay);
    video.addEventListener('playing',  onPlaying);
    video.addEventListener('stalled',  onStalled);

    return () => {
      video.removeEventListener('waiting',  onWaiting);
      video.removeEventListener('canplay',  onCanPlay);
      video.removeEventListener('playing',  onPlaying);
      video.removeEventListener('stalled',  onStalled);
    };
  }, []);

  // Play / Pause based on active state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      // Always restart from the beginning, just like Instagram/YouTube Shorts
      video.currentTime = 0;
      setIsBuffering(true);  // show spinner immediately while video loads
      // Start muted first — browser autoplay policy requires this
      video.muted = true;

      const p = video.play();
      if (p !== undefined) {
        p.then(() => {
          video.muted = isMuted;
          setIsPlaying(true);
        }).catch(() => {
          video.muted = true;
          video.play().then(() => setIsPlaying(true)).catch(() => {});
        });
      }
      addView();
    } else {
      video.pause();
      video.currentTime = 0; // reset so it's ready to start fresh next time
      setIsPlaying(false);
    }
  }, [isActive]);

  // Sync mute state whenever user toggles the volume button
  useEffect(() => {
    if (videoRef.current && isActive) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, isActive]);

  const addView = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ reel_id: reel.id.toString() })
      });
    } catch (_) {}
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) { video.pause(); setIsPlaying(false); }
    else { video.play(); setIsPlaying(true); }
  };

  const handleTap = (e) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    if (now - lastTap.current < DOUBLE_TAP_DELAY) {
      // Double tap → like
      if (!isLiked) handleLike();
      setShowHeart(true);
      setTimeout(() => setShowHeart(false), 800);
    } else {
      // Single tap after short delay to distinguish from double
      setTimeout(() => {
        if (Date.now() - lastTap.current >= DOUBLE_TAP_DELAY) {
          togglePlay();
        }
      }, DOUBLE_TAP_DELAY);
    }
    lastTap.current = now;
  };

  const handleLike = () => {
    setIsLiked(prev => !prev);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const videoSrc = reel.video_url?.startsWith('http')
    ? reel.video_url
    : `https://www.whysocial.in/clap-kartel-reels-module/public/${reel.video_url || reel.video}`;

  return (
    <div className="reel-item" onClick={handleTap}>
      {/* Video */}
      <video
        ref={videoRef}
        className="reel-video"
        src={videoSrc}
        loop
        playsInline
        preload="auto"
        poster={reel.thumbnail_url || reel.thumbnail}
      />

      {/* Buffering spinner — shown while video data is loading */}
      <AnimatePresence>
        {isBuffering && (
          <motion.div
            className="reel-buffer-spinner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="reel-spinner-ring" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Play icon overlay — only show when paused AND not buffering */}
      <AnimatePresence>
        {!isPlaying && !isBuffering && (
          <motion.div
            className="reel-play-overlay"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 0.15 }}
          >
            <FaPlay />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Double-tap heart burst */}
      <AnimatePresence>
        {showHeart && (
          <motion.div
            className="reel-heart-burst"
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1.4 }}
            exit={{ opacity: 0, scale: 1.8 }}
            transition={{ duration: 0.4 }}
          >
            <FaHeart />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right action bar */}
      <div className="reel-actions">
        {/* Like */}
        <div className="reel-action-item" onClick={(e) => { e.stopPropagation(); handleLike(); }}>
          <FaHeart size={26} color={isLiked ? '#ff3b5c' : 'white'} className={isLiked ? 'liked-icon' : ''} />
          <span>{likesCount}</span>
        </div>

        {/* Comment */}
        <div className="reel-action-item" onClick={(e) => { e.stopPropagation(); onOpenComments(reel); }}>
          <FaComment size={24} color="white" />
          <span>{reel.comments_count || 0}</span>
        </div>

        {/* Share */}
        <div className="reel-action-item">
          <FaShare size={22} color="white" />
          <span>Share</span>
        </div>
      </div>

      {/* Bottom info */}
      <div className="reel-bottom-info">
        <div className="reel-user-row">
          <img
            className="reel-user-avatar"
            src={reel.user_profile_image
              ? `https://www.whysocial.in/clap-kartel/public/${reel.user_profile_image}`
              : DEFAULT_AVATAR}
            alt={reel.uploader_name}
            onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
          />
          <span className="reel-username">@{reel.uploader_name || reel.user_name || 'anonymous'}</span>
        </div>
        {reel.title && (
          <p className="reel-title-text">{reel.title}</p>
        )}
        {reel.description && (
          <p className="reel-desc-text">{reel.description}</p>
        )}
        <span className="reel-views">{reel.views_count || 0} views</span>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Comments Bottom Sheet
// ──────────────────────────────────────────────
const CommentsSheet = ({ isOpen, reel, onClose, comments, loading, newText, setNewText, onPost }) => {
  return (
    <>
      {/* Dim overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="comments-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sheet itself */}
      <div className={`comments-sheet ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="comments-handle" />
        <div className="comments-header">
          <span className="comments-title">Comments</span>
          <button className="comments-close-btn" onClick={onClose}><IoClose size={22} /></button>
        </div>

        <div className="comments-list">
          {loading ? (
            <div className="comments-loading"><FaSpinner className="spin" /> Loading...</div>
          ) : comments.length === 0 ? (
            <div className="comments-empty">No comments yet. Be the first!</div>
          ) : (
            comments.map((c, i) => (
              <div className="comment-row" key={c.id || i}>
                <img
                  className="comment-avatar"
                  src={c.user_profile_image || DEFAULT_AVATAR}
                  alt={c.user_name}
                  onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                />
                <div className="comment-body">
                  <span className="comment-name">{c.user_name || 'Anonymous'}</span>
                  <p className="comment-text">{c.comment}</p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="comments-input-row">
          <input
            type="text"
            placeholder="Add a comment..."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onPost()}
          />
          <button onClick={onPost} disabled={!newText.trim()}>Post</button>
        </div>
      </div>
    </>
  );
};

// ──────────────────────────────────────────────
// Upload Modal
// ──────────────────────────────────────────────
const UploadModal = ({ isOpen, onClose, onSubmit, uploadData, setUploadData, onFileChange, isUploading }) => {
  if (!isOpen) return null;
  return (
    <div className="upload-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upload-header">
          <h2>Upload Reel</h2>
          <button onClick={onClose}><IoClose size={22} /></button>
        </div>
        <form onSubmit={onSubmit} className="upload-form">
          <label>Title</label>
          <input type="text" required value={uploadData.title} onChange={(e) => setUploadData(p => ({ ...p, title: e.target.value }))} placeholder="Title" />
          <label>Description</label>
          <textarea required value={uploadData.description} onChange={(e) => setUploadData(p => ({ ...p, description: e.target.value }))} rows="3" placeholder="Description" />
          <label>Duration (seconds)</label>
          <input type="number" required value={uploadData.duration} onChange={(e) => setUploadData(p => ({ ...p, duration: e.target.value }))} placeholder="e.g. 15" />
          <label>Video File</label>
          <input type="file" accept="video/*" required onChange={onFileChange} />
          <button type="submit" className="upload-submit-btn" disabled={isUploading}>
            {isUploading ? <><FaSpinner className="spin" /> Uploading...</> : 'Upload Reel'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Main Reel Page
// ──────────────────────────────────────────────
const Reel = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  // Hide the global Header / Navbar / Footer while on this page
  useEffect(() => {
    document.body.classList.add('reels-active');
    return () => document.body.classList.remove('reels-active');
  }, []);

  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const reelsLengthRef = useRef(0);
  const [isMuted, setIsMuted] = useState(false); // Sound ON by default

  // Comments state
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [activeReel, setActiveReel] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', description: '', duration: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Sync refs
  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);
  useEffect(() => { reelsLengthRef.current = reels.length; }, [reels]);

  // ── Fetch reels ──
  useEffect(() => { fetchReels(); }, []);

  const fetchReels = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/feed', {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      let list = [];
      if (data.status && data.data?.reels?.length) list = data.data.reels;
      else if (data.status && data.data?.data?.length) list = data.data.data;
      else if (data.status && Array.isArray(data.data) && data.data.length) list = data.data;
      else list = [
        { id: 1, title: 'Demo Reel', uploader_name: 'demo_user', description: 'Scroll down for more #demo', video_url: 'https://www.w3schools.com/html/mov_bbb.mp4', likes_count: 42, comments_count: 5, views_count: 320 },
        { id: 2, title: 'Another Reel', uploader_name: 'filmmaker', description: 'Another great reel #bts', video_url: 'https://www.w3schools.com/html/mov_bbb.mp4', likes_count: 99, comments_count: 12, views_count: 780 },
      ];
      setReels(list);
    } catch (_) { setReels([]); }
    finally { setLoading(false); }
  };

  // ── Wheel scroll (desktop) — strictly one reel per scroll action ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Lock flag — prevents any new scroll until the current one fully settles
    let isLocked = false;

    const releaseLock = () => { isLocked = false; };

    const scrollToReel = (idx) => {
      isLocked = true;
      container.scrollTo({ top: idx * container.clientHeight, behavior: 'smooth' });

      // Primary: release lock when the browser signals scroll animation is done
      if ('onscrollend' in window) {
        container.addEventListener('scrollend', releaseLock, { once: true });
      } else {
        // Fallback for browsers without scrollend (Safari < 17, older Chrome)
        // Poll until the scroll position stops changing, then release
        let lastTop = -1;
        let stableCount = 0;
        const poll = () => {
          if (container.scrollTop === lastTop) {
            stableCount++;
            if (stableCount >= 3) { releaseLock(); return; } // stable for 3 frames
          } else {
            stableCount = 0;
            lastTop = container.scrollTop;
          }
          requestAnimationFrame(poll);
        };
        requestAnimationFrame(poll);
      }
    };

    const onWheel = (e) => {
      e.preventDefault(); // always block native scroll
      if (isLocked) return; // drop event — transition still in progress
      if (Math.abs(e.deltaY) < 5) return; // ignore micro-movements

      const cur = activeIndexRef.current;
      const len = reelsLengthRef.current;
      let next = cur;
      if (e.deltaY > 0 && cur < len - 1) next = cur + 1;      // scroll down
      else if (e.deltaY < 0 && cur > 0) next = cur - 1;        // scroll up

      if (next !== cur) scrollToReel(next);
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('scrollend', releaseLock);
    };
  }, []);

  // ── IntersectionObserver — track which reel is in view ──
  useEffect(() => {
    const slides = containerRef.current?.querySelectorAll('.reel-slide');
    if (!slides || slides.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = parseInt(entry.target.dataset.index, 10);
            if (!isNaN(idx) && idx !== activeIndexRef.current) {
              setActiveIndex(idx);
              activeIndexRef.current = idx;
              setCommentsOpen(false);
            }
          }
        });
      },
      { threshold: 0.6 }
    );

    slides.forEach((slide) => observer.observe(slide));
    return () => observer.disconnect();
  }, [reels]); // Re-run when reels load

  // ── Comments ──
  const openComments = useCallback((reel) => {
    setActiveReel(reel);
    setCommentsOpen(true);
    fetchComments(reel.id);
  }, []);

  const closeComments = useCallback(() => {
    setCommentsOpen(false);
    setComments([]);
    setNewComment('');
  }, []);

  const fetchComments = async (reelId) => {
    setCommentsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/comments/${reelId}`, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setComments(data.status && data.data ? data.data : []);
    } catch (_) { setComments([]); }
    finally { setCommentsLoading(false); }
  };

  const postComment = async () => {
    if (!newComment.trim() || !activeReel) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/comment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ reel_id: activeReel.id, comment: newComment.trim() })
      });
      const data = await res.json();
      if (data.status) {
        showToast('Comment posted!');
        setNewComment('');
        fetchComments(activeReel.id);
        setReels(prev => prev.map(r => r.id === activeReel.id ? { ...r, comments_count: (parseInt(r.comments_count) || 0) + 1 } : r));
      } else showToast('Failed to post comment', 'error');
    } catch (_) { showToast('Error posting comment', 'error'); }
  };

  // ── Upload ──
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    const vid = document.createElement('video');
    vid.preload = 'metadata';
    vid.src = URL.createObjectURL(file);
    vid.onloadedmetadata = () => {
      URL.revokeObjectURL(vid.src);
      setUploadData(p => ({ ...p, duration: Math.round(vid.duration).toString() }));
    };
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { showToast('Select a video first', 'error'); return; }
    setIsUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('title', uploadData.title);
      fd.append('description', uploadData.description);
      fd.append('duration', uploadData.duration);
      fd.append('video', uploadFile);
      const res = await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/upload', {
        method: 'POST', headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }, body: fd
      });
      const data = await res.json();
      if (data.status || res.ok) {
        showToast('Reel uploaded!');
        setUploadOpen(false);
        setUploadData({ title: '', description: '', duration: '' });
        setUploadFile(null);
        fetchReels();
      } else showToast(data.message || 'Upload failed', 'error');
    } catch (_) { showToast('Upload error', 'error'); }
    finally { setIsUploading(false); }
  };

  // ── Render ──
  if (loading) {
    return (
      <div className="reels-loading-screen">
        <FaSpinner className="spin" size={32} />
        <p>Loading Reels...</p>
      </div>
    );
  }

  return (
    <div className="reels-page">
      {/* Top bar */}
      <div className="reels-topbar">
        <button className="reels-back-btn" onClick={() => navigate('/')}><IoArrowBack size={22} /></button>
        <span className="reels-topbar-title">Reels</span>
        <button className="reels-upload-topbar-btn" onClick={() => setUploadOpen(true)}><FaPlus size={18} /></button>
      </div>

      {/* Full-screen scroll container */}
      <div className="reels-feed" ref={containerRef}>
        {reels.length === 0 ? (
          <div className="reels-empty-screen">No reels yet.</div>
        ) : (
          reels.map((reel, i) => (
            <div className="reel-slide" key={reel.id || i} data-index={i}>
              {/* Desktop: centered 9:16 card with sidebar */}
              <div className="reel-slide-inner">
                <div className="reel-card">
                  <ReelItem
                    reel={reel}
                    isActive={i === activeIndex}
                    isMuted={isMuted}
                    onToggleMute={() => setIsMuted(p => !p)}
                    onOpenComments={openComments}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Comments sheet (portal-like, rendered at root of reels-page) */}
      <CommentsSheet
        isOpen={commentsOpen}
        reel={activeReel}
        onClose={closeComments}
        comments={comments}
        loading={commentsLoading}
        newText={newComment}
        setNewText={setNewComment}
        onPost={postComment}
      />

      {/* Upload modal */}
      <UploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={handleUpload}
        uploadData={uploadData}
        setUploadData={setUploadData}
        onFileChange={handleFileChange}
        isUploading={isUploading}
      />

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className={`reels-toast ${toast.type}`}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Reel;
